import React, { forwardRef } from 'react';
import UISpinner from '@/components/shared/UISpinner'; // Import UISpinner
import './UIButton.scss';

export interface UIButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'size'> {
  /**
   * Варіант кольору кнопки
   */
  variant?:
    | 'primary'
    | 'secondary'
    | 'info'
    | 'warning'
    | 'danger'
    | 'success'
    | 'dark'
    | 'light';

  /**
   * Розмір кнопки
   */
  size?: 'small' | 'medium' | 'large';

  /**
   * Outline стиль (прозорий фон з рамкою)
   */
  outline?: boolean;

  /**
   * Повний width батьківського контейнера
   */
  fullWidth?: boolean;

  /**
   * Стан завантаження (показує спіннер)
   */
  loading?: boolean;

  /**
   * Заблокований стан
   */
  disabled?: boolean;

  /**
   * Іконка на початку кнопки
   */
  startIcon?: React.ReactNode;

  /**
   * Іконка в кінці кнопки
   */
  endIcon?: React.ReactNode;

  /**
   * Кнопка тільки з іконкою (без тексту)
   */
  iconOnly?: boolean;

  /**
   * Додатковий CSS клас
   */
  className?: string;

  /**
   * Вміст кнопки
   */
  children?: React.ReactNode;

  /**
   * HTML тип кнопки
   */
  type?: 'button' | 'submit' | 'reset';

  /**
   * Використовувати як посилання
   */
  href?: string;

  /**
   * Target для посилання
   */
  target?: string;


  minWidth?: string | number;

  /**
   * Обробник кліку
   */
  onClick?: (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => void;
}

const baseClass = 'UIButton';

const UIButton = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  UIButtonProps
>(
  (
    {
      variant = 'primary', // Default variant
      size = 'medium', // Default size
      outline = false, // Default outline
      fullWidth = false, // Default fullWidth
      loading = false, // Default loading
      disabled = false, // Default disabled
      startIcon,
      endIcon,
      iconOnly = false, // Default iconOnly
      className = '',
      children,
      type = 'button', // Default type
      href,
      minWidth,
      target,
      onClick,
      ...props
    },
    ref,
  ) => {
    const containerClasses = [
      baseClass,
      `${baseClass}--${variant}`,
      `${baseClass}--${size}`,
      outline && `${baseClass}--outline`,
      fullWidth && `${baseClass}--full-width`,
      loading && `${baseClass}--loading`,
      disabled && `${baseClass}--disabled`,
      iconOnly && `${baseClass}--icon-only`,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const handleClick = (
      event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
    ) => {
      if (!disabled && !loading) {
        onClick?.(event);
      }
    };

    const buttonStyle = minWidth
      ? { minWidth: typeof minWidth === 'number' ? `${minWidth}px` : minWidth }
      : undefined;

    const renderContent = () => {
      if (loading) {
        return <UISpinner size="small" />; // Render spinner when loading
      }

      if (iconOnly) {
        return startIcon || endIcon || children;
      }

      return (
        <>
          {startIcon && (
            <span className={`${baseClass}__icon ${baseClass}__icon--start`}>
              {startIcon}
            </span>
          )}
          {children}
          {endIcon && (
            <span className={`${baseClass}__icon ${baseClass}__icon--end`}>
              {endIcon}
            </span>
          )}
        </>
      );
    };

    // Якщо передано href - рендеримо як посилання
    if (href) {
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          type={type} // type is not a valid prop for <a>, but keeping it for consistency if it was intended for some custom logic
          style={buttonStyle}
          target={target}
          className={containerClasses}
          onClick={handleClick}
          aria-disabled={disabled || loading}
          {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
        >
          {renderContent()}
        </a>
      );
    }

    // Інакше рендеримо як кнопку
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type={type}
        className={containerClasses}
        disabled={disabled || loading}
        onClick={handleClick}
        {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      >
        {renderContent()}
      </button>
    );
  },
);

UIButton.displayName = 'UIButton';

export default UIButton;
